import { useEffect, useState } from "react";
import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Input, Select } from "@/components";
import type { ApiDeviceBranding } from "../../_types/device-brandings.types";
import {
  BRANDING_PROTOTYPE_ASSET_OPTIONS,
  createPrototypeBrandingId,
} from "../../_constants/device-branding-prototypes.const";
import styles from "./CreateBrandDrawer.module.scss";

type CreateBrandDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (brand: ApiDeviceBranding) => void;
};

const NONE = "";

export const CreateBrandDrawer = ({ isOpen, onClose, onCreate }: CreateBrandDrawerProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [wallpaperAssetId, setWallpaperAssetId] = useState(NONE);
  const [logoAssetId, setLogoAssetId] = useState(NONE);
  const [alertSoundAssetId, setAlertSoundAssetId] = useState(NONE);
  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setDescription("");
    setDisplayName("");
    setAccentColor("");
    setWallpaperAssetId(NONE);
    setLogoAssetId(NONE);
    setAlertSoundAssetId(NONE);
    setNameError("");
    setSubmitError(null);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (name.trim().length < 1) {
      setNameError("Name is required");
      return;
    }
    setNameError("");
    setSubmitError(null);
    const accent = accentColor.trim();
    const accentOk =
      accent === "" || /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(accent);
    if (!accentOk) {
      setSubmitError("Accent color must be empty or a hex like #RRGGBB or #RRGGBBAA.");
      return;
    }
    const now = Date.now();
    onCreate({
      id: createPrototypeBrandingId(),
      name: name.trim(),
      description: description.trim() || null,
      displayName: displayName.trim() || null,
      accentColor: accent || null,
      wallpaperAssetId: wallpaperAssetId || null,
      logoAssetId: logoAssetId || null,
      alertSoundAssetId: alertSoundAssetId || null,
      extra: { prototype: true },
      configVersion: 1,
      createdAt: now,
      updatedAt: now,
    });
    onClose();
  };

  const imageAssets = BRANDING_PROTOTYPE_ASSET_OPTIONS.filter((a) => a.kind === "image");
  const audioAssets = BRANDING_PROTOTYPE_ASSET_OPTIONS.filter((a) => a.kind === "audio");

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Create branding (prototype)">
      <DrawerHeader title="Create branding (Prototype)" onClose={handleClose} />
      <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
        <DrawerBody>
          <div className={styles["comp"]}>
            <p className={styles["comp__hint"]}>
              Prototype / Preview — sample assets only. Nothing is uploaded or saved to the API, and
              branding here cannot be assigned to real devices.
            </p>
            <div className={styles["comp__fields"]}>
              <Input
                id="create-brand-name"
                label="Name"
                value={name}
                placeholder="e.g. Vetty Kiosk — default"
                required
                error={nameError}
                onChange={(v) => {
                  setName(v);
                  setNameError("");
                  setSubmitError(null);
                }}
              />

              <Input
                id="create-brand-description"
                label="Description"
                value={description}
                placeholder="Optional"
                onChange={(v) => setDescription(v)}
              />

              <Input
                id="create-brand-display-name"
                label="Display name on device"
                value={displayName}
                placeholder="Optional"
                onChange={(v) => setDisplayName(v)}
              />

              <Input
                id="create-brand-accent"
                label="Accent color (hex)"
                value={accentColor}
                placeholder="#ff541f"
                onChange={(v) => setAccentColor(v)}
              />

              <Select
                id="create-brand-wallpaper"
                label="Wallpaper (sample)"
                fullWidth
                size="medium"
                variant="secondary"
                value={wallpaperAssetId}
                onChange={(v) => setWallpaperAssetId(String(v))}
              >
                <option value={NONE}>None</option>
                {imageAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>

              <Select
                id="create-brand-logo"
                label="Logo (sample)"
                fullWidth
                size="medium"
                variant="secondary"
                value={logoAssetId}
                onChange={(v) => setLogoAssetId(String(v))}
              >
                <option value={NONE}>None</option>
                {imageAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>

              <Select
                id="create-brand-alert"
                label="Alert sound (sample)"
                fullWidth
                size="medium"
                variant="secondary"
                value={alertSoundAssetId}
                onChange={(v) => setAlertSoundAssetId(String(v))}
              >
                <option value={NONE}>None</option>
                {audioAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>
            </div>

            {submitError ? <div className={styles["comp__error"]}>{submitError}</div> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Create branding
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};
